import { Injectable } from '@angular/core';

import { of } from 'rxjs';
import { map, mergeMap, catchError, concatMap, withLatestFrom } from 'rxjs/operators';

import { createEffect, ofType, Actions } from '@ngrx/effects';
import { RouterReducerState } from '@ngrx/router-store';
import { Store, select } from '@ngrx/store';

import { RouterStateUrl, ReferenceService } from '@sd-portal/shared/util';

import * as actions from './staff-event.actions';
import { State } from '..';
import { StaffEventAuditService } from '../../staff-event-audit.service';

@Injectable()
export class StaffEventEffects {
  constructor(
    private readonly staffEventAuditService: StaffEventAuditService,
    private readonly referenceService: ReferenceService,
    private readonly actions$: Actions,
    private readonly store: Store<{
      router: RouterReducerState<RouterStateUrl>;
      sharedAudit: State;
    }>
  ) {}
  // Skeleton

  loadStaffEventSkeletons$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadStaffEventSkeletons),
      mergeMap(() =>
        this.staffEventAuditService.getStaffEventSkeletons().pipe(
          map(payload => actions.StaffEventSkeletonsLoaded({ payload })),
          catchError(err => of(actions.StaffEventSkeletonsLoadError(err)))
        )
      )
    )
  );

  //StaffEvent
  loadStaffEventAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadStaffEventAudit),
      mergeMap(action =>
        this.staffEventAuditService.getStaffEventAudits(action.staffEventId).pipe(
          map(audits =>
            actions.StaffEventAuditLoaded({ staffEventId: action.staffEventId, audits })
          ),
          catchError(err => of(actions.StaffEventAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageStaffEventAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageStaffEventAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.staffEvent.staffEventAudit.lastAuditLogId;
        }
        return this.staffEventAuditService
          .getStaffEventAudits(action.staffEventId, nextPageNumber, undefined, lastAuditLogId)
          .pipe(
            map(audits =>
              actions.StaffEventAuditLoaded({ staffEventId: action.staffEventId, audits })
            ),
            catchError(err => of(actions.StaffEventAuditLoadError(err)))
          );
      })
    )
  );

  //StaffEventBusinessPerson
  loadStaffEventBusinessPersonAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadStaffEventBusinessPersonAudit),
      mergeMap(action =>
        this.staffEventAuditService
          .getStaffEventBusinessPersonAudits(action.staffEventBusinessPersonId)
          .pipe(
            map(audits =>
              actions.StaffEventBusinessPersonAuditLoaded({
                staffEventBusinessPersonId: action.staffEventBusinessPersonId,
                audits
              })
            ),
            catchError(err => of(actions.StaffEventBusinessPersonAuditLoadError(err)))
          )
      )
    )
  );

  loadNextPageStaffEventBusinessPersonAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageStaffEventBusinessPersonAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId =
            state.sharedAudit.staffEvent.staffEventBusinessPersonAudit.lastAuditLogId;
        }
        return this.staffEventAuditService
          .getStaffEventBusinessPersonAudits(
            action.staffEventBusinessPersonId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.StaffEventBusinessPersonAuditLoaded({
                staffEventBusinessPersonId: action.staffEventBusinessPersonId,
                audits
              })
            ),
            catchError(err => of(actions.StaffEventBusinessPersonAuditLoadError(err)))
          );
      })
    )
  );

  //ScheduledEvent
  loadScheduledEventAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadScheduledEventAudit),
      mergeMap(action =>
        this.staffEventAuditService.getScheduledEventAudits(action.scheduledEventId).pipe(
          map(audits =>
            actions.ScheduledEventAuditLoaded({
              scheduledEventId: action.scheduledEventId,
              audits
            })
          ),
          catchError(err => of(actions.ScheduledEventAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageScheduledEventAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageScheduledEventAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.staffEvent.scheduledEventAudit.lastAuditLogId;
        }
        return this.staffEventAuditService
          .getScheduledEventAudits(
            action.scheduledEventId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.ScheduledEventAuditLoaded({
                scheduledEventId: action.scheduledEventId,
                audits
              })
            ),
            catchError(err => of(actions.ScheduledEventAuditLoadError(err)))
          );
      })
    )
  );

  loadStaffEventTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadStaffEventTypes),
      mergeMap(() =>
        this.referenceService.getReferenceItemsNoCache('StaffEventType', false).pipe(
          map(payload => actions.StaffEventTypesLoaded({ payload })),
          catchError(err => of(actions.StaffEventTypesLoadError(err)))
        )
      )
    )
  );
}
